"use client"

import { ReactNode, useEffect, useState } from "react";
import { FilterBar, FilterInputTemplate } from "../filter/FilterBar";
import { FilterSearchPayload, DataTable, DataTableColumn } from "../table/DataTable";
import { Button, message } from "antd";
import { DatabaseRecord, TableRepo } from "@/core/repos/table.repo";
import { FileExcelOutlined } from "@ant-design/icons";
import { useUpdate } from "@/core/hooks/use-update.hook";
import { useLocation } from "react-router";

export interface FilterChangesPayload {
    filterTemplates?: FilterInputTemplate[],
    filterMap?: Map<string, FilterSearchPayload>
}

export interface ReportTableConfigs<T extends DatabaseRecord, S> {
    columns: DataTableColumn<T>[]
    initialFilterMap?: Map<string, FilterSearchPayload>,
    filterTemplates: FilterInputTemplate[],
    onFilterChanged: (map: Map<string, FilterSearchPayload>, changes: string[], templates: FilterInputTemplate[]) => Promise<FilterChangesPayload>,
    init: (templates: FilterInputTemplate[]) => Promise<FilterChangesPayload>,
    handleExport: (filterMap: Map<string, FilterSearchPayload>, defaultFilter: object) => Promise<void>,
    repository: TableRepo<T>
    getSummary?: (filterMap: Map<string, FilterSearchPayload>) => Promise<S>
    renderSummary?: (summary: S) => ReactNode
    defaultFilter?: object
}

export interface ReportTableProps<T extends DatabaseRecord, S> {
    reportIndex?: number
    config: ReportTableConfigs<T, S>
}
export type ReportTable<T extends DatabaseRecord, S> = React.FC<ReportTableProps<T, S>>
export const ReportTable = <T extends DatabaseRecord, S>(props: ReportTableProps<T, S>) => {
    const {reportIndex, config} = props; 
    const location = useLocation();
    // Parse query params to initial filterMap
    function getInitialFilterMap(): Map<string, FilterSearchPayload> {
        const params = new URLSearchParams(location.search);
        const map = new Map(config.initialFilterMap);
        params.forEach((value, key) => {
            // Always treat value as string, do not try to parse as array
            map.set(key, { key, value });
        });
        return map;
    }
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>(config.filterTemplates)
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(getInitialFilterMap())
    const [summary, setSummary] = useState<S>();
    const [isLoading, setLoading] = useState(false);

    useEffect(()=>{
        setFilterTemplates(config.filterTemplates);
        initTemplates(config.filterTemplates);
        setFilterMap(getInitialFilterMap());
        // Reset query params in the URL after initial filter data are processed
        if (window && window.history && window.location) {
            const url = window.location.pathname;
            window.history.replaceState({}, '', url);
        }
    }, [config.filterTemplates, location.search])

    const getSummary = async (map: Map<string, FilterSearchPayload>) => {
        if (config.getSummary) {
            const _summary = await config.getSummary(map);
            setSummary(_summary);
        }
    }

    const renderSummary = () => {
        if (config.renderSummary&&summary) {
            return config.renderSummary(summary);
        }
        return null;
    }

    const initTemplates = async (_templates: FilterInputTemplate[]) => {
        const result = await config.init(_templates);
        if (result.filterTemplates) {
            setFilterTemplates(result.filterTemplates);
        }
    }

    useEffect(() => {
        getSummary(filterMap);
    }, [filterMap, reportIndex]);

    async function onFilterChange(map: Map<string, FilterSearchPayload>, changes: string[]) {
        const filterChanges = await config.onFilterChanged(map, changes, filterTemplates);
        console.log(filterChanges);
        if (filterChanges.filterTemplates) {
            setFilterTemplates(filterChanges.filterTemplates)
        }
        if (filterChanges.filterMap) {
            setFilterMap(filterChanges.filterMap);
        }
        setFilterMap(new Map(map));
    }

    async function handleExport() {
        setLoading(true);
        message.info("Xuất báo cáo nhiều bản ghi sẽ mất vài phút. Xin vui lòng đợi!");
        await config.handleExport(filterMap, config.defaultFilter??{});
        setLoading(false);
    }

    return (
    <div className="px-4 py-6">
       <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap} actions={[
            <div className="flex flex-row" key="1">
                <Button type="primary" loading={isLoading} icon={<FileExcelOutlined />} style={{ background: "#29A339"}} onClick={handleExport}>Xuất Excel</Button>
            </div>
        ]}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <div>
                <DataTable<T>
                    columns={config.columns}
                    repository={config.repository}
                    filterMap={filterMap}
                    onSelect={() => {}}
                    hideSelection
                    summary={renderSummary}
                    defaultParams={config.defaultFilter}
                ></DataTable>
            </div>
        </div>
    </div>)
}